import React from "react"

import { Label } from "../../components/shared/Inputs"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { useToast } from "../../contexts/ToastContext"
import { resolveImagePickerHandle } from "../../utils/resolveImagePickerHandle"
import ImagePickerV2 from "../WebsiteBuilderV2/components/shared/ImagePickerV2"

import { useBookableEventTypes } from "./BookableEventTypesContext"

const EVENT_CARD_IMAGE_ASPECT_RATIO = 396 / 250

const PhotoUpload = ({ practice = {} }) => {
  const { bookableEventType, setBookableEventType, fetching, removePhoto, updateBookableEventTypes } =
    useBookableEventTypes()

  const { showToast } = useToast()

  const handleImageSelect = ({ handle, filestackPhoto, imageUrl }) => {
    setBookableEventType((prev) => ({
      ...prev,
      photoUrl: imageUrl,
      stagedPhotoHandle: resolveImagePickerHandle({ handle, filestackPhoto })
    }))
    showToast("Photo selected. It will be applied when you save.")
  }

  const onPhotoDelete = () => {
    // If a photo is staged, just remove it from state.
    if (bookableEventType.stagedPhotoHandle) {
      setBookableEventType((prev) => {
        const next = { ...prev }
        delete next.stagedPhotoHandle
        delete next.photoUrl
        return next
      })
      showToast("Staged photo was removed.")
      return
    }

    // If it's a persisted photo, call the backend to delete it.
    removePhoto({ id: bookableEventType.id }).then((result) => {
      if (result?.data?.removeBookableEventTypePhoto?.result === "success") {
        const newEventType = { ...bookableEventType, photoUrl: null }
        setBookableEventType(newEventType)
        updateBookableEventTypes(newEventType)
        showToast("Your photo has been removed.")
      } else {
        console.error(result) // eslint-disable-line no-console
        let errorMessage =
          "There was an error removing your event type photo. Please try again later or contact support."
        if (result.data?.removeBookableEventTypePhoto?.errors)
          errorMessage += ` ${result.data.removeBookableEventTypePhoto.errors}`
        showToast({
          type: "error",
          content: errorMessage
        })
      }
    })
  }

  return (
    <>
      <div className="flex justify-between">
        <Label htmlFor="photo">Photo</Label>
        {!bookableEventType.photoUrl && <div className="text-sm text-gray-dark">Optional</div>}
      </div>
      {fetching ? (
        <LoadingSpinner className="py-0" />
      ) : bookableEventType.photoUrl ? (
        <div className="flex items-center gap-4">
          <img
            src={bookableEventType.photoUrl}
            alt="Event type photo"
            className="h-16 rounded-lg object-cover"
            style={{ aspectRatio: String(EVENT_CARD_IMAGE_ASPECT_RATIO) }}
          />
          <div className="flex flex-col gap-2">
            <ImagePickerV2
              practice={practice}
              onImageSelect={handleImageSelect}
              fetching={fetching}
              showCurateTab={true}
              currentImageUrl={bookableEventType.photoUrl}
              trigger={
                <button className="font-bold text-teal hover:underline" disabled={fetching}>
                  Edit photo
                </button>
              }
            />
            <button className="font-bold text-red hover:underline" onClick={onPhotoDelete}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <ImagePickerV2
          practice={practice}
          onImageSelect={handleImageSelect}
          fetching={fetching}
          showCurateTab={true}
          trigger={
            <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-light bg-white p-6 transition-colors hover:border-teal">
              <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-teal/[0.12]">
                <svg className="size-5 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <p className="text-center text-base font-bold text-teal">upload an image</p>
              <p className="text-center text-sm text-gray-dark">Supported formats: PNG, JPG, SVG</p>
            </div>
          }
        />
      )}
    </>
  )
}

export default PhotoUpload
